/**
 * Server-Sent Events (SSE) Service
 * Handles real-time event streaming from the backend
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

export type SSEEventType =
  | 'message.created'
  | 'message.updated'
  | 'message.deleted'
  | 'reaction.added'
  | 'reaction.removed'
  | 'typing.started'
  | 'typing.stopped'
  | 'participant.added'
  | 'participant.removed'
  | 'participant.updated'
  | 'heartbeat';

export interface SSEEvent<T = unknown> {
  id: string;
  event: SSEEventType;
  data: T;
}

export type SSEEventHandler = (event: SSEEvent) => void;

export class SSEService {
  private eventSource: EventSource | null = null;
  private reconnectTimeout: number | null = null;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private handlers: Map<SSEEventType | 'all', Set<SSEEventHandler>> = new Map();
  private conversationId: string | null = null;
  private lastEventId: string | null = null;

  /**
   * Connect to SSE stream for a conversation
   * Uses cookie-based authentication (credentials: 'include')
   */
  async connect(conversationId: string): Promise<void> {
    if (this.eventSource) {
      this.disconnect();
    }

    this.conversationId = conversationId;

    await this.createEventSource();
  }

  /**
   * Create EventSource connection
   */
  private async createEventSource(): Promise<void> {
    if (!this.conversationId) {
      console.error('[SSE] Cannot connect without conversation ID');
      return;
    }

    try {
      // Fetch short-lived SSE token from backend using cookie auth
      console.log('[SSE] Fetching SSE token...');
      const tokenResponse = await fetch(`${API_URL}/v1/sse/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Use cookie-based auth
        body: JSON.stringify({
          conversation_id: this.conversationId,
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to fetch SSE token');
      }

      const tokenData = await tokenResponse.json();
      const sseToken = tokenData.token;

      console.log('[SSE] SSE token obtained, connecting...');

      const url = new URL(
        `${API_URL}/v1/conversations/${this.conversationId}/events`,
        window.location.origin
      );

      // Add short-lived SSE token as query parameter
      url.searchParams.set('token', sseToken);

      // Add last event ID for resumption
      if (this.lastEventId) {
        url.searchParams.set('since', this.lastEventId);
      }

      console.log('[SSE] Connecting to:', url.toString());

      this.eventSource = new EventSource(url.toString());

      // Handle connection open
      this.eventSource.onopen = () => {
        console.log('[SSE] Connected');
        this.reconnectDelay = 1000; // Reset reconnect delay on successful connection
      };

      // Handle generic messages
      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const sseEvent: SSEEvent = {
            id: event.lastEventId || '',
            event: 'heartbeat', // Default to heartbeat if no type specified
            data,
          };

          if (event.lastEventId) {
            this.lastEventId = event.lastEventId;
          }

          this.emitEvent(sseEvent);
        } catch (error) {
          console.error('[SSE] Failed to parse message:', error);
        }
      };

      // Handle typed events
      this.setupEventListeners();

      // Handle errors
      this.eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);

        // Close the connection
        this.eventSource?.close();
        this.eventSource = null;

        // Attempt to reconnect with exponential backoff
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[SSE] Failed to create event source:', error);
      // Schedule reconnect on token fetch failure
      this.scheduleReconnect();
    }
  }

  /**
   * Setup listeners for specific event types
   */
  private setupEventListeners(): void {
    if (!this.eventSource) return;

    const eventTypes: SSEEventType[] = [
      'message.created',
      'message.updated',
      'message.deleted',
      'reaction.added',
      'reaction.removed',
      'typing.started',
      'typing.stopped',
      'participant.added',
      'participant.removed',
      'participant.updated',
    ];

    eventTypes.forEach((eventType) => {
      this.eventSource!.addEventListener(eventType, (event: Event) => {
        const messageEvent = event as MessageEvent;
        try {
          const data = JSON.parse(messageEvent.data);
          const sseEvent: SSEEvent = {
            id: messageEvent.lastEventId || '',
            event: eventType,
            data,
          };

          if (messageEvent.lastEventId) {
            this.lastEventId = messageEvent.lastEventId;
          }

          this.emitEvent(sseEvent);
        } catch (error) {
          console.error(`[SSE] Failed to parse ${eventType} event:`, error);
        }
      });
    });
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      return; // Already scheduled
    }

    console.log(`[SSE] Reconnecting in ${this.reconnectDelay}ms...`);

    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectTimeout = null;
      this.createEventSource();

      // Increase delay for next attempt (exponential backoff)
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
    }, this.reconnectDelay);
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.conversationId = null;
    this.lastEventId = null;
    this.reconnectDelay = 1000;

    console.log('[SSE] Disconnected');
  }

  /**
   * Register event handler
   */
  on(eventType: SSEEventType | 'all', handler: SSEEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }

    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Emit event to registered handlers
   */
  private emitEvent(event: SSEEvent): void {
    // Call specific event handlers
    const specificHandlers = this.handlers.get(event.event);
    if (specificHandlers) {
      specificHandlers.forEach((handler) => handler(event));
    }

    // Call 'all' handlers
    const allHandlers = this.handlers.get('all');
    if (allHandlers) {
      allHandlers.forEach((handler) => handler(event));
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }
}

// Export singleton instance
export const sseService = new SSEService();
